/* ============================================================
   SINGSPACE ADMIN - MODAL DÉTAILS CLIENT (Pro Max)
   ============================================================ */

import {
  Activity,
  AlertCircle,
  Calendar,
  Clock,
  DollarSign,
  History,
  Mail,
  Package,
  Phone,
  TrendingUp,
  User,
  X,
} from 'lucide-react';
import { useClientDetails } from '../../hooks/useUsers';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Button } from '../ui/button';

interface ClientDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
}

export function ClientDetailsModal({ isOpen, onClose, clientId }: ClientDetailsModalProps) {
  const { client, loading, error } = useClientDetails(clientId);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-[40px] shadow-2xl max-w-4xl w-full overflow-hidden border border-white/20 animate-in zoom-in-95 slide-in-from-bottom-4 duration-500 max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER */}
        <div className="bg-[#00758d] p-8 text-white relative flex-shrink-0">
          <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -mr-24 -mt-24" />
          <div className="flex justify-between items-start relative z-10">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center font-black text-xl shadow-inner">
                {client ? (client.firstName[0] + client.lastName[0]).toUpperCase() : '?'}
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-2 py-0.5 rounded-md bg-white/20 text-[10px] font-black uppercase tracking-widest">
                    Fiche Client
                  </span>
                  {client?.isActive && (
                    <span className="px-2 py-0.5 rounded-md bg-emerald-400/20 text-emerald-100 text-[10px] font-black uppercase tracking-widest">
                      Actif
                    </span>
                  )}
                </div>
                <h2 className="text-3xl font-black tracking-tight">
                  {client ? `${client.firstName} ${client.lastName}` : 'Chargement...'}
                </h2>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-8 custom-scrollbar">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <LoadingSpinner message="Extraction des données client..." submessage="" size="md" />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-20 text-rose-500">
              <AlertCircle size={40} className="mb-4 opacity-50" />
              <p className="font-bold text-sm">{error}</p>
            </div>
          ) : client ? (
            <div className="space-y-8">
              {/* GRID INFO & STATS */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* COORDONNÉES */}
                <div className="lg:col-span-2 space-y-6">
                  <SectionTitle title="Coordonnées & Informations" icon={User} color="#00758d" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50/50 p-6 rounded-3xl border border-slate-100">
                    <InfoItem label="Email" value={client.email} icon={Mail} />
                    <InfoItem label="Téléphone" value={client.phone} icon={Phone} />
                    <InfoItem
                      label="Inscrit le"
                      value={new Date(client.createdAt).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                      icon={Calendar}
                    />
                    <InfoItem
                      label="Statut du compte"
                      value={client.isActive ? 'Opérationnel' : 'Suspendu'}
                      icon={Activity}
                      className={client.isActive ? 'text-emerald-600' : 'text-rose-600'}
                    />
                  </div>
                </div>

                {/* QUICK STATS */}
                <div className="space-y-6">
                  <SectionTitle title="Aperçu Activité" icon={TrendingUp} color="#dfc32f" />
                  <div className="space-y-3">
                    <StatPill
                      label="Réservations"
                      value={String(client.bookingsCount || 0)}
                      icon={Package}
                      color="#00758d"
                    />
                    <StatPill label="Total Dépensé" value="0 FCFA" icon={DollarSign} color="#8E0B56" />
                    <StatPill label="En cours" value="0" icon={Clock} color="#dfc32f" />
                  </div>
                </div>
              </div>

              {/* HISTORIQUE RÉCENT */}
              <div className="space-y-6">
                <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                  <SectionTitle title="Historique des réservations" icon={History} color="#00303c" />
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    Derniers 12 mois
                  </span>
                </div>

                <div className="p-12 border-4 border-dashed border-slate-50 rounded-[32px] flex flex-col items-center text-center">
                  <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4 text-slate-200">
                    <Package size={32} />
                  </div>
                  <h4 className="text-slate-400 font-bold text-sm">Aucune donnée de réservation</h4>
                  <p className="text-slate-300 text-xs mt-1">
                    Le client n'a pas encore effectué de transaction sur la plateforme.
                  </p>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        <div className="p-6 bg-slate-50 border-t border-slate-100 flex justify-end flex-shrink-0">
          <Button
            onClick={onClose}
            className="px-8 h-12 rounded-2xl bg-[#00303c] hover:bg-black text-white font-black shadow-lg transition-all"
          >
            Fermer la fiche
          </Button>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ title, icon: Icon, color }: { title: string; icon: any; color: string }) {
  return (
    <div className="flex items-center gap-3">
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center shadow-sm"
        style={{ backgroundColor: `${color}15`, color }}
      >
        <Icon size={16} />
      </div>
      <h3 className="font-black text-[#00303c] uppercase tracking-tight text-xs">{title}</h3>
    </div>
  );
}

function InfoItem({
  label,
  value,
  icon: Icon,
  className = '',
}: {
  label: string;
  value: string;
  icon: any;
  className?: string;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2 opacity-40">
        <Icon size={12} />
        <span className="text-[9px] font-black uppercase tracking-widest">{label}</span>
      </div>
      <p className={`font-bold text-sm text-[#00303c] truncate ${className}`}>{value}</p>
    </div>
  );
}

function StatPill({ label, value, icon: Icon, color }: { label: string; value: string; icon: any; color: string }) {
  return (
    <div className="flex items-center justify-between p-4 bg-white border border-slate-100 rounded-2xl shadow-sm group hover:border-slate-200 transition-all">
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
          style={{ backgroundColor: `${color}10`, color }}
        >
          <Icon size={20} />
        </div>
        <span className="text-xs font-bold text-slate-500">{label}</span>
      </div>
      <span className="text-lg font-black text-[#00303c] font-mono tracking-tight">{value}</span>
    </div>
  );
}
